import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { initializationFinished, loggedOut, profileLoaded } from '@/features/auth/authSlice';
import { contextLoaded, organizationSelected } from '@/features/tenant/tenantSlice';
import { useLazyMeQuery, useOrganizationContextQuery } from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { LoadingState } from '@/components/ui';

/**
 * Resolves the session before anything protected renders.
 *
 * Holding the shell until the profile call settles avoids the flash of a login
 * screen on reload for a user who is, in fact, signed in.
 */
export function AuthGate() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { accessToken, initialized, user } = useAppSelector((state) => state.auth);
  const [fetchMe] = useLazyMeQuery();

  useEffect(() => {
    if (initialized) return;

    if (!accessToken) {
      dispatch(initializationFinished());
      return;
    }

    void fetchMe()
      .unwrap()
      .then((result) => dispatch(profileLoaded(result)))
      .catch(() => dispatch(initializationFinished()));
  }, [accessToken, dispatch, fetchMe, initialized]);

  if (!initialized) return <FullPageLoader label="Restoring your session…" />;

  if (!accessToken || !user) {
    return <Navigate to="/auth/login" replace state={{ from: location.pathname }} />;
  }

  return <Outlet />;
}

/**
 * Loads the tenant context — permissions, features, branches, settings — and
 * holds rendering until it is available, so no screen ever renders with an
 * unknown permission set.
 */
export function TenantGate() {
  const dispatch = useAppDispatch();
  const organizationId = useAppSelector((state) => state.tenant.organizationId);
  const organizations = useAppSelector((state) => state.auth.organizations);
  const context = useAppSelector((state) => state.tenant.context);

  // Nothing selected yet: fall back to the first business the user belongs to.
  useEffect(() => {
    if (organizationId) return;
    const first = organizations[0];
    if (first) dispatch(organizationSelected(first.id));
  }, [dispatch, organizationId, organizations]);

  const { data, isLoading, isError, error } = useOrganizationContextQuery(undefined, {
    skip: !organizationId,
  });

  useEffect(() => {
    if (data) dispatch(contextLoaded(data));
  }, [data, dispatch]);

  if (organizations.length === 0) return <Navigate to="/onboarding" replace />;
  if (!organizationId || (isLoading && !context)) {
    return <FullPageLoader label="Loading your business…" />;
  }

  if (isError && !context) {
    const status = (error as { status?: number } | undefined)?.status;

    // A business that is gone is the only case where creating one is the right
    // next step. Being refused is different: sending someone who has been
    // suspended to "set up your business" tells them the opposite of the truth.
    if (status === 404) return <Navigate to="/onboarding" replace />;
    if (status === 403) {
      return (
        <AccessRefused
          message={apiErrorMessage(error, 'Your access to this business is not active')}
        />
      );
    }
    return <FullPageLoader label="Retrying…" />;
  }

  if (!context) return <FullPageLoader label="Loading your business…" />;

  return <Outlet />;
}

function FullPageLoader({ label }: { label: string }) {
  return (
    <div className="grid h-screen place-items-center">
      <LoadingState label={label} />
    </div>
  );
}

/**
 * Shown when the business is there but this member may not reach it — usually
 * a suspended account. Signing out is the only way forward, so it is the only
 * thing offered.
 */
function AccessRefused({ message }: { message: string }) {
  const dispatch = useAppDispatch();

  return (
    <div className="grid h-screen place-items-center px-6">
      <div className="max-w-sm text-center">
        <h1 className="text-lg font-semibold">You cannot open this business</h1>
        <p className="mt-2 text-sm text-secondary">{message}</p>
        <button
          type="button"
          onClick={() => dispatch(loggedOut())}
          className="mt-6 rounded-lg border border-subtle px-4 py-2 text-sm font-medium hover:bg-sunken"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
